import { useEffect, useRef, useState } from "react";
import { ImagePlus, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLook } from "@/context/LookContext";
import { api } from "@/lib/api";
import { blobUrl } from "@/lib/authedImage";
import { WeatherBadge } from "@/components/weather/WeatherCard";

function useTodayEvents(hub, active) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!active || !hub?.my_access?.calendar) { setList(null); return; }
    const d = new Intl.DateTimeFormat("en-CA", { timeZone: hub.timezone || undefined }).format(new Date());
    api.get(`/organizations/${hub.id}/calendar/events`, { params: { start: d, end: d } }).then(({ data }) => setList(data.events)).catch(() => setList(null));
  }, [hub, active]);
  return list;
}

const evTime = (e) => (e.all_day ? "All day" : new Date(e.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));

const EVENTS = ["mousemove", "mousedown", "pointerdown", "keydown", "touchstart", "wheel", "scroll"];
const GRACE_MS = 700;

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const greeting = (h) => (h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening");

function Clock({ big = true }) {
  const now = useNow();
  return (
    <div className="text-white" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.55)" }}>
      <div className={`font-display font-extrabold leading-none tracking-tight ${big ? "text-7xl sm:text-8xl lg:text-9xl" : "text-6xl sm:text-7xl"}`} data-testid="screensaver-clock">
        {now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
      </div>
      <div className="mt-3 text-lg font-semibold text-white/85 sm:text-2xl" data-testid="screensaver-date">
        {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
      </div>
    </div>
  );
}

function usePhotos(hubId, active) {
  const [urls, setUrls] = useState(null);
  useEffect(() => {
    if (!active) return;
    if (!hubId) { setUrls([]); return; }
    let live = true;
    api.get(`/organizations/${hubId}/photos`)
      .then(({ data }) => Promise.all(data.photos.slice(0, 40).map((p) => blobUrl(`/organizations/${hubId}/photos/${p.id}/file`).catch(() => null))))
      .then((list) => live && setUrls(list.filter(Boolean)))
      .catch(() => live && setUrls([]));
    return () => { live = false; };
  }, [hubId, active]);
  return urls;
}

function Backdrop({ url }) {
  return url
    ? <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${url}")` }} />
    : <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 20%, rgba(var(--glow-home),0.55), transparent 55%), radial-gradient(circle at 80% 80%, rgba(var(--glow-family),0.5), transparent 55%), rgb(var(--q-bg))" }} />;
}

function Slideshow({ urls, wallpaperUrl }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!urls || urls.length < 2) return;
    const id = setInterval(() => setI((n) => (n + 1) % urls.length), 8000);
    return () => clearInterval(id);
  }, [urls]);
  if (urls === null) return <Backdrop url={wallpaperUrl} />;
  if (!urls.length) {
    return (
      <>
        <Backdrop url={wallpaperUrl} />
        <div className="absolute inset-x-0 bottom-10 flex justify-center px-6">
          <p className="inline-flex items-center gap-2 rounded-full bg-black/45 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur-md" data-testid="screensaver-empty-hint">
            <ImagePlus className="h-4 w-4" /> Add family photos in My Look → Screensaver
          </p>
        </div>
      </>
    );
  }
  return urls.map((u, n) => (
    <div key={u} className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ease-in-out"
      style={{ backgroundImage: `url("${u}")`, opacity: n === i ? 1 : 0 }} data-testid={`screensaver-photo-${n}`} data-visible={n === i ? "1" : "0"} />
  ));
}

function useChoresLeft(hub, active) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!active || !hub?.my_access?.kids) { setList(null); return; }
    api.get(`/organizations/${hub.id}/kids/summary`).then(({ data }) => setList(data.chores_left_today)).catch(() => setList(null));
  }, [hub, active]);
  return list;
}

function TodayView({ name, wallpaperUrl, chores, showKid, events, hubId }) {
  const now = useNow();
  return (
    <>
      <Backdrop url={wallpaperUrl} />
      <div className="absolute inset-0 bg-black/45" />
      <div className="relative flex h-full flex-col justify-center gap-10 px-8 sm:px-16 lg:flex-row lg:items-center lg:justify-between lg:px-24">
        <div>
          <p className="mb-6 font-display text-2xl font-bold text-white/90 sm:text-3xl" data-testid="screensaver-greeting">{greeting(now.getHours())}, {name}</p>
          <Clock />
          {hubId && <WeatherBadge hubId={hubId} />}
        </div>
        <div className="w-full max-w-sm rounded-2xl border border-white/15 bg-black/35 p-6 text-white backdrop-blur-xl" data-testid="screensaver-today-panel">
          <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-white/70"><Sparkles className="h-3.5 w-3.5" /> Today</p>
          {chores && (
            <div className="mb-4" data-testid="screensaver-today-chores">
              <p className="mb-2 text-sm font-bold text-white">{chores.length ? `Chores left today · ${chores.length}` : "All chores done today!"}</p>
              <ul className="space-y-1.5">
                {chores.slice(0, 8).map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-base text-white/90" data-testid={`screensaver-chore-${c.id}`}>
                    <span className="text-lg">{c.icon}</span><span className="truncate">{c.title}{showKid ? ` · ${c.kid_name}` : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {events && (
            <div data-testid="screensaver-today-events">
              <p className="mb-2 text-sm font-bold text-white">{events.length ? `Events today · ${events.length}` : "No events today"}</p>
              <ul className="space-y-1.5">
                {events.slice(0, 8).map((e) => (
                  <li key={e.id} className="flex items-center gap-2 text-base text-white/90" data-testid={`screensaver-event-${e.id.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                    <span className="w-20 flex-shrink-0 text-sm tabular-nums text-white/70">{evTime(e)}</span><span className="truncate">{e.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Full-screen overlay on top of the app; the page underneath stays mounted so forms keep their input.
export default function Screensaver({ hub }) {
  const { session } = useAuth();
  const { look, wallpaperUrl, screensaver, startScreensaver, stopScreensaver } = useLook();
  const ss = look?.screensaver;
  const last = useRef(Date.now());
  const state = useRef(screensaver);
  state.current = screensaver;

  useEffect(() => {
    const onInput = (e) => {
      last.current = Date.now();
      const s = state.current;
      if (!s.on || Date.now() - s.since < GRACE_MS) return;
      if (e.type === "keydown" || e.type === "touchstart" || e.type === "mousedown" || e.type === "pointerdown") e.preventDefault();
      if (e.type === "keydown") e.stopPropagation();
      stopScreensaver();
    };
    EVENTS.forEach((t) => window.addEventListener(t, onInput, { capture: true, passive: false }));
    return () => EVENTS.forEach((t) => window.removeEventListener(t, onInput, { capture: true }));
  }, [stopScreensaver]);

  useEffect(() => {
    if (!ss?.enabled) { stopScreensaver(); return; }
    last.current = Date.now();
    const id = setInterval(() => {
      if (!state.current.on && Date.now() - last.current >= ss.idle_minutes * 60000) startScreensaver();
    }, 500);
    return () => clearInterval(id);
  }, [ss?.enabled, ss?.idle_minutes, startScreensaver, stopScreensaver]);

  const active = screensaver.on && !!ss?.enabled;
  const photos = usePhotos(hub?.id, active && ss?.mode === "photos");
  const chores = useChoresLeft(hub, active && ss?.mode === "clock");
  const events = useTodayEvents(hub, active && ss?.mode === "clock");
  if (!active) return null;
  const name = (session?.type === "kid" ? session.kid.name : session?.user?.name || "").split(" ")[0];

  return (
    <div className="fixed inset-0 z-[200] cursor-none overflow-hidden bg-black q-rise" data-testid="screensaver-overlay" data-mode={ss.mode}>
      {ss.mode === "clock" ? <TodayView name={name} wallpaperUrl={wallpaperUrl} chores={chores} events={events} hubId={hub?.id} showKid={session?.type !== "kid"} /> : (
        <>
          <Slideshow urls={photos} wallpaperUrl={wallpaperUrl} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
          <div className="absolute bottom-20 left-8 sm:left-16 lg:left-24"><Clock />{hub?.id && <WeatherBadge hubId={hub.id} />}</div>
        </>
      )}
    </div>
  );
}
